import { useCallback, useEffect, useState } from 'react';
import { createGitlabArtifact, readGitlabState } from '../../api/endpoints';
import { ApiError, toApiError } from '../../api/errors';
import type { Id } from '../../api/types';
import { Notice } from '../../components/ui/Notice';
import { codeActionsFor, labelFor, type CodeAction, type GitlabCodeState } from './code-actions';

/**
 * PH-62 — the GitLab section on a ticket: what exists, and what can be created.
 *
 * 🔴 THE GAP THIS CLOSES. `POST /tickets/:id/gitlab/branch` and `/gitlab/merge-request` have worked
 *   since PH-8 and NOTHING IN THE PRODUCT CALLED EITHER — two thirds of that ticket's title were
 *   unreachable. The issue path is live and proven (PH-29 created GitLab issue #1).
 *
 * ⛔⛔ ABSENT, NOT DISABLED, when the project is not linked. 8 of 9 production projects have
 *   `gitlab_project_id = null`, so unlinked is the COMMON case: a greyed-out button here would put a
 *   dead control on almost every ticket and offer no way to learn why. The decision lives in
 *   `codeActionsFor`, which is tested on its own — this component only renders it.
 *
 * ⚠️ FAILURES ARE SHOWN WITH THE API'S OWN SENTENCE. `requireRuntime()` answers
 *   *"the GitLab write path is not configured on this deployment"* and the project check answers
 *   *"this ticket's project has no GitLab project linked"*. Those sentences are the only part that
 *   tells the reader what to do next; a friendlier paraphrase would delete it.
 */
export function GitlabCodePanel({
  ticketId,
  projectGitlabId,
  issueIid,
}: {
  ticketId: Id;
  /** From the ticket's project. ⛔ null ⇒ this whole component renders nothing. */
  projectGitlabId: Id | null;
  issueIid: Id | null;
}) {
  const [state, setState] = useState<GitlabCodeState | null>(null);
  const [busy, setBusy] = useState<CodeAction | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    if (projectGitlabId === null) return;
    try {
      setState(await readGitlabState(ticketId));
    } catch (err) {
      // ⛔ NOT swallowed into an empty state. "We could not ask" and "nothing exists" are different
      //    facts, and an empty state here would offer buttons based on the wrong one.
      setError(toApiError(err, `/tickets/${ticketId}/gitlab`));
    }
  }, [ticketId, projectGitlabId]);

  useEffect(() => {
    void load();
  }, [load]);

  const view = codeActionsFor(state, projectGitlabId);
  if (!view.show) return null;

  const run = async (action: CodeAction) => {
    setBusy(action);
    setError(null);
    try {
      await createGitlabArtifact(ticketId, action);
      // ⭐ Re-read rather than assume success created what we asked for. The API returns 200 for
      //   "already existed" and 201 for "created", and either way the truth is what it now reports.
      await load();
    } catch (err) {
      setError(toApiError(err, `/tickets/${ticketId}/gitlab`));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="space-y-2 border-t border-[color:var(--color-line)] pt-3">
      <h3 className="text-sm font-semibold">GitLab</h3>

      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-[color:var(--color-ink-muted)]">Issue</dt>
        <dd className="font-mono text-xs">{issueIid ? `#${issueIid}` : '—'}</dd>
        <dt className="text-[color:var(--color-ink-muted)]">Branch</dt>
        <dd className="break-all font-mono text-xs">{state?.branchName ?? '—'}</dd>
        <dt className="text-[color:var(--color-ink-muted)]">Merge request</dt>
        <dd className="font-mono text-xs">
          {state?.mergeRequestIid ? `!${state.mergeRequestIid}` : '—'}
        </dd>
      </dl>

      {/* ⚠️ Shown INSTEAD of buttons, never alongside them. See codeActionsFor: when GitLab is
          unreachable, `branchName === null` means "we could not ask", and offering a write on that
          reading sends a request at a GitLab we cannot see. */}
      {view.staleWarning ? (
        <Notice tone="warning" title="GitLab could not be reached">
          <p className="break-words">{view.staleWarning}</p>
        </Notice>
      ) : null}

      {error ? (
        <Notice tone="error" title="The API refused this">
          {/* The API's own sentence, verbatim. */}
          <p className="break-words">{error.reason ?? error.message}</p>
        </Notice>
      ) : null}

      {view.offered.length > 0 ? (
        <div className="flex flex-col gap-2 sm:flex-row">
          {view.offered.map((action) => (
            <button
              key={action}
              type="button"
              disabled={busy !== null}
              onClick={() => void run(action)}
              className="h-11 rounded border border-[color:var(--color-line)] px-3 text-sm disabled:opacity-50 sm:h-9"
            >
              {busy === action ? 'Working…' : labelFor(action)}
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
